import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  ColumnInstance,
  HeaderGroup,
  TableInstance,
  useFilters,
  useGlobalFilter,
  useSortBy,
  UseSortByColumnProps,
  useTable,
} from 'react-table';
import './CardDateTable.scss';
import { Column } from 'react-table';
import { CardDateItemState } from '../../features/rod-lift-analysis/CardDateSlice';
import { useAppSelector } from '../../hooks/storeHooks';
import Loader from '../../features/common/Loader/Loader';
import { DefaultColumnFilter } from './helpers/DefaultColumnFilter';
import { ReactComponent as MagnifyingGlassIcon } from '../../images/magnifying-glass.svg';
import { FixedSizeList as FixedSizeList } from 'react-window';
import { createMultiConditionFilter } from '../../utilities/tableFilters';
import { ALLY_CONFIGURATIONS } from '../../services/configuration/config';
import { showSuccessToast, showWarningAlert } from '../toast/toastBridge';
import { formatDate } from '../../utilities/dateHelpers';

interface CardDateTableProps {
  cardDateItems: CardDateItemState[];
  rowSelectionClick: (index: number, date: string) => void;
  rowBactchSelectionClick: (date: string[]) => void;
  triggerExport?: boolean;
  viewCardAnalysis: (rowData: CardDateItemState) => void;
  visibleColumns?: string[];
  applyTrigger?: boolean;
  isResetClicked?: boolean;
  isFullscreen?: boolean;
}

const splitHeader = (text: string) => {
  const [line1, line2] = text.split(' | ');
  return (
    <div className='flex flex-col leading-tight'>
      <span>{line1}</span>
      {line2 && <span>{line2}</span>}
    </div>
  );
};

type SortByState = { sortBy?: any[] };

const CardDateTable: React.FC<CardDateTableProps> = ({
  cardDateItems,
  rowSelectionClick,
  rowBactchSelectionClick,
  triggerExport,
  viewCardAnalysis,
  visibleColumns,
  applyTrigger,
  isResetClicked,
  isFullscreen = false,
}) => {
  const data = useMemo(() => cardDateItems, [cardDateItems]);
  const isLoadingCards = useAppSelector((state) => state.rodLiftAnalysis.isLoading);
  const hasTriggeredInitialClick = useRef(false);
  const listRef = useRef<any>(null);
  const sortByRef = useRef<any[]>([]);
  const [currentFilters, setCurrentFilters] = useState<any[]>([]);
  const currentFiltersRef = useRef(currentFilters);
  const [filteredColumns, setFilteredColumns] = useState<Column<CardDateItemState>[]>([]);
  const [listHeight, setListHeight] = useState<number>(500);
  const DEFAULT_LIST_HEIGHT = 500;
  const FULLSCREEN_MIN_HEIGHT = 300;
  const FULLSCREEN_PADDING = 32;
  const selectedAssetId = useAppSelector((state) => state.assetGroups?.selectedAssetId);
  const MAX_CARDS_SELECTABLE = ALLY_CONFIGURATIONS.MAX_CARDS_SELECTABLE;
  const highlightedRow = useAppSelector((state: any) => state.rodLiftAnalysis?.highlightRow);
  const [isChartFullScreen, setIsChartFullScreen] = useState<boolean>(false);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleFullscreenChange = () =>
      setIsChartFullScreen(
        !!document.fullscreenElement && document.fullscreenElement.classList.contains('card-viewer'),
      );
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);
  useEffect(() => {
    currentFiltersRef.current = currentFilters; // Keep the ref updated with the latest value of currentFilters
  }, [currentFilters]);

  const multiConditionFilter = createMultiConditionFilter(setCurrentFilters, currentFiltersRef);

  const updateHeightForFullscreen = React.useCallback(() => {
    if (isFullscreen) {
      const headerWrapEl = document.querySelector('.card-header-wrap');
      const headerWrapHeight = headerWrapEl && headerWrapEl instanceof HTMLElement ? headerWrapEl.clientHeight : 0;
      const tableHeaderEl = document.querySelector('.table-header-wrapper');
      const tableHeaderHeight = tableHeaderEl && tableHeaderEl instanceof HTMLElement ? tableHeaderEl.clientHeight : 0;
      const newHeight = Math.max(
        FULLSCREEN_MIN_HEIGHT,
        window.innerHeight - headerWrapHeight - tableHeaderHeight - FULLSCREEN_PADDING,
      );
      setListHeight(newHeight);
    } else {
      setListHeight(DEFAULT_LIST_HEIGHT);
    }
  }, [isFullscreen]);

  useEffect(() => {
    updateHeightForFullscreen();
    if (isFullscreen) {
      window.addEventListener('resize', updateHeightForFullscreen);
    }
    return () => {
      window.removeEventListener('resize', updateHeightForFullscreen);
    };
  }, [isFullscreen, updateHeightForFullscreen]);

  const selectedCount = data?.filter((item) => item.isSelected).length || 0;
  const maxSelectableForCurrentData = Math.min(data?.length || 0, MAX_CARDS_SELECTABLE);
  const allSelected = (data?.length || 0) > 0 && selectedCount === maxSelectableForCurrentData;
  const someSelected = selectedCount > 0 && selectedCount < maxSelectableForCurrentData;

  const handleSelectAll = () => {
    const alreadySelectedCardCount = data.filter((item) => item.isSelected).length;

    // If all cards are selected then unselect all
    if (allSelected) {
      const selectedDates = data.filter((item) => item.isSelected).map((item) => item.date);
      if (selectedDates.length > 0) {
        rowBactchSelectionClick(selectedDates);
      }
      return;
    }

    if (alreadySelectedCardCount >= maxSelectableForCurrentData) {
      showWarningAlert(`You can add only ${maxSelectableForCurrentData} cards to the chart.`);
      return;
    }

    // If no cards are selected, select up to the maximum allowed
    const selectedDates: string[] = [];
    data.forEach((item) => {
      if (selectedDates.length < maxSelectableForCurrentData - alreadySelectedCardCount && !item.isSelected) {
        selectedDates.push(item.date);
      }
    });
    const cardsToAdd = maxSelectableForCurrentData - alreadySelectedCardCount;
    showSuccessToast(
      `${cardsToAdd} cards have been added`,
      `A maximum of ${MAX_CARDS_SELECTABLE} cards can be added to the chart`,
    );
    rowBactchSelectionClick(selectedDates);
  };

  const allColumns: Column<CardDateItemState>[] = useMemo(
    () => [
      {
        Header: () => (
          <input
            type='checkbox'
            checked={allSelected}
            disabled={data.length === 0}
            ref={(el) => {
              if (el) el.indeterminate = !allSelected && someSelected;
            }}
            onChange={handleSelectAll}
            className='card-table-checkbox'
            title={
              data.length === 0
                ? 'No cards available to select'
                : allSelected
                  ? `Unselect all ${selectedCount} cards`
                  : `Select ${maxSelectableForCurrentData} cards`
            }
          />
        ),
        accessor: 'isSelected',
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ row }: { row: { original: CardDateItemState; index: number } }) => (
          <input
            type='checkbox'
            checked={row.original.isSelected}
            readOnly
            onClick={() => rowSelectionClick(row.index, row.original.date)}
            className='card-table-checkbox'
          />
        ),
      },
      {
        Header: '',
        accessor: 'color',
        disableSortBy: true,
        Cell: ({ value, row }: { value: { r?: number; g?: number; b?: number }; row: { original: CardDateItemState } }) => {
          const isValidColor =
            value && typeof value.r === 'number' && typeof value.g === 'number' && typeof value.b === 'number';
          const isSelected = row.original.isSelected;

          const style =
            isSelected && isValidColor ? { backgroundColor: `rgb(${value.r}, ${value.g}, ${value.b})` } : {};

          const classNameDynamoCir = `dynamo-circle-color ${isSelected && isValidColor ? 'filled' : 'hollow'}`;

          return (
            <div className='dynamo-cir-analysis-wrapper'>
              <div className={classNameDynamoCir} style={style} />
              <MagnifyingGlassIcon className='cursor-pointer' onClick={() => viewCardAnalysis(row.original)} />
            </div>
          );
        },
      },
      {
        Header: 'Date / time',
        accessor: 'date' as const,
        filter: multiConditionFilter,
        Cell: ({ value }: { value: string }) => formatDate(value, true),
      },
      { Header: splitHeader('Card type'), accessor: 'cardTypeName', filter: multiConditionFilter },
      { Header: splitHeader('DH stroke | (in)'), accessor: 'downHoleStroke', filter: multiConditionFilter },
      { Header: splitHeader('Pump efficiency | (%)'), accessor: 'pumpEfficiency', filter: multiConditionFilter },
      { Header: splitHeader('Pump fillage | (%)'), accessor: 'pumpFillage', filter: multiConditionFilter },
      {
        Header: splitHeader('Pump fillage | setpoint (%)'),
        accessor: 'pumpFillageSetpoint',
        filter: multiConditionFilter,
      },
      {
        Header: splitHeader('Secondary fillage | setpoint (%)'),
        accessor: 'secondaryFillageSetpoint',
        filter: multiConditionFilter,
      },
      {
        Header: splitHeader('Inferred | production (bpd)'),
        accessor: 'inferredProduction',
        filter: multiConditionFilter,
      },
      { Header: splitHeader('Runtime | (hrs)'), accessor: 'runtime', filter: multiConditionFilter },
      { Header: 'SPM', accessor: 'strokesPerMinute', filter: multiConditionFilter },
      {
        Header: splitHeader('Min polished | rod load (lbs)'),
        accessor: 'minPolishedRodLoad',
        filter: multiConditionFilter,
      },
      {
        Header: splitHeader('Max polished | rod load (lbs)'),
        accessor: 'maxPolishedRodLoad',
        filter: multiConditionFilter,
      },
      { Header: splitHeader('Fluid load | (lbs)'), accessor: 'fluidPound', filter: multiConditionFilter },
      { Header: splitHeader('Buoyant rod | weight (lbs)'), accessor: 'buoyantRodWeight', filter: multiConditionFilter },
      { Header: splitHeader('Dry rod | weight (lbs)'), accessor: 'dryRodWeight', filter: multiConditionFilter },
      { Header: splitHeader('Pump friction | load (lbs)'), accessor: 'pumpFriction', filter: multiConditionFilter },
      { Header: splitHeader('PO fluid | load (lbs)'), accessor: 'poFluidLoad', filter: multiConditionFilter },
    ] as unknown as Column<CardDateItemState>[],
    [rowSelectionClick, viewCardAnalysis],
  );

  // Apply column filtering based on visibleColumns prop
  const columns = useMemo(() => {
    if (!visibleColumns || visibleColumns.length === 0) {
      return allColumns; // Return all columns if no filtering is specified
    }

    // Always include mandatory columns first (isSelected, color, date)
    const mandatoryColumns = ['isSelected', 'color', 'date'];
    const mandatoryColumnObjects = allColumns.filter((col) => {
      const colKey = 'accessor' in col ? col.accessor : col.id;
      return mandatoryColumns.includes(colKey as string);
    });

    // Get configurable columns based on visibleColumns
    const configurableColumnObjects = allColumns.filter((col) => {
      const colKey = 'accessor' in col ? col.accessor : col.id;
      return !mandatoryColumns.includes(colKey as string) && visibleColumns.includes(colKey as string);
    });

    return [...mandatoryColumnObjects, ...configurableColumnObjects];
  }, [allColumns, visibleColumns]);

  // Apply column reordering based on visibleColumns order (but keep mandatory columns first)
  const orderedColumns = useMemo(() => {
    if (!visibleColumns || visibleColumns.length === 0) {
      return columns;
    }

    const mandatoryColumns = ['isSelected', 'color', 'date'];

    // Separate mandatory and configurable columns
    const mandatoryColumnObjects = columns.filter((col) => {
      const colKey = 'accessor' in col ? col.accessor : col.id;
      return mandatoryColumns.includes(colKey as string);
    });

    const configurableColumnObjects = columns.filter((col) => {
      const colKey = 'accessor' in col ? col.accessor : col.id;
      return !mandatoryColumns.includes(colKey as string);
    });

    // Sort mandatory columns in the correct order (isSelected, color, date)
    const sortedMandatoryColumns = mandatoryColumnObjects.sort((a, b) => {
      const aKey = 'accessor' in a ? a.accessor : a.id;
      const bKey = 'accessor' in b ? b.accessor : b.id;
      return mandatoryColumns.indexOf(aKey as string) - mandatoryColumns.indexOf(bKey as string);
    });

    // Sort only the configurable columns based on visibleColumns order
    const sortedConfigurableColumns = configurableColumnObjects.sort((a, b) => {
      const aKey = 'accessor' in a ? a.accessor : a.id;
      const bKey = 'accessor' in b ? b.accessor : b.id;
      return visibleColumns.indexOf(aKey as string) - visibleColumns.indexOf(bKey as string);
    });

    // Return mandatory columns first, then sorted configurable columns
    return [...sortedMandatoryColumns, ...sortedConfigurableColumns];
  }, [columns, visibleColumns]);

  // Update filtered columns when applyTrigger or isResetClicked changes
  useEffect(() => {
    setFilteredColumns(orderedColumns);
  }, [orderedColumns, applyTrigger, isResetClicked]);

  const defaultColumn: any = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: (props: any) => (
        <DefaultColumnFilter
          {...props}
          clearFilterForColumn={clearFilterForColumn}
          currentFiltersRef={currentFiltersRef}
          isFullscreen={isFullscreen}
        />
      ),
    }),
    [isFullscreen],
  );
  const instance = useTable<CardDateItemState>(
    {
      columns: filteredColumns,
      data,
      defaultColumn: defaultColumn,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
  );

  const {
    getTableProps,
    headerGroups,
    rows,
    prepareRow,
    // setGlobalFilter,
    //setFilter,
    setAllFilters,
    setSortBy,
  } = instance as TableInstance<CardDateItemState> & {
    setGlobalFilter: (filterValue: string | undefined) => void;
    //setFilter: (columnId: string, filterValue: any) => void;
    setAllFilters: (filters: any[]) => void;
    setSortBy: (sortBy: any[]) => void;
  };

  const clearFilterForColumn = (columnId: string) => {
    const remainingFilters = currentFiltersRef.current.filter((filter) => filter.columnId !== columnId);
    // Remove the filter for the specified column ID
    setCurrentFilters(remainingFilters);

    if (remainingFilters.length === 0) {
      setAllFilters([]); // Clear all filters if no remaining filters
    } else {
      setAllFilters(
        remainingFilters.map((filter) => ({
          id: filter.columnId,
          value: filter.filterValue,
        })),
      );
    }
    // remainingFilters.forEach((filter) => {
    //   // Apply the remaining filters to the table
    //   setFilter(filter.columnId, filter.filterValue);
    // });
  };

  // useEffect(() => {
  //   if (!isLoadingCards) {
  //     hasTriggeredInitialClick.current = false;
  //   }
  // }, [isLoadingCards]);

  useEffect(() => {
    const state = instance.state as SortByState;
    sortByRef.current = state.sortBy || [];
  }, [instance.state]);

  // Reapply filters when data changes
  React.useLayoutEffect(() => {
    if (currentFiltersRef.current.length > 0) {
      setAllFilters(
        currentFiltersRef.current.map((filter) => ({
          id: filter.columnId,
          value: filter.filterValue,
        })),
      );
    }
    if (sortByRef.current && setSortBy) {
      setSortBy(sortByRef.current);
    }
  }, [data, setAllFilters, setSortBy]);

  useEffect(() => {
    if (data.length > 0 && !hasTriggeredInitialClick.current) {
      hasTriggeredInitialClick.current = true;
      rowSelectionClick(0, data[0].date);
    }
  }, [data, rowSelectionClick]);

  useEffect(() => {
    if (!triggerExport) return;
    const headers = headerGroups[0].headers
      .map((col: any) => {
        const header = col.render('Header');
        if (typeof header === 'string') return header;
        if (header?.props?.children) {
          if (Array.isArray(header.props.children)) {
            return header.props.children
              .map((child: any) => (typeof child === 'string' ? child : child?.props?.children || ''))
              .join(' ');
          }
          return typeof header.props.children === 'string'
            ? header.props.children
            : header.props.children?.props?.children || '';
        }
        return '';
      })
      .slice(2);
    const rowsData = rows.map((row) => {
      prepareRow(row);
      return row.cells
        .map((cell) => {
          const value = cell.value;
          if (value === null || value === undefined) return '';
          if (typeof value === 'object') {
            return '';
          }
          if (cell.column.id === 'date') {
            return formatDate(value, true);
          }
          return value;
        })
        .slice(2);
    });

    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0].replace(/-/g, '');
    const fileName = `${selectedAssetId}-RL-Card-Viewer-${formattedDate}.xlsx`;
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rowsData]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Card Data');
    XLSX.writeFile(wb, fileName);
  }, [triggerExport]);

  const columnWidths: Record<string, number> = {
    isSelected: 40,
    color: 60,
    date: 170,
    cardTypeName: 90,
    downHoleStroke: 120,
    pumpEfficiency: 120,
    pumpFillage: 120,
    pumpFillageSetpoint: 110,
    secondaryFillageSetpoint: 130,
    inferredProduction: 140,
    runtime: 80,
    strokesPerMinute: 80,
    minPolishedRodLoad: 120,
    maxPolishedRodLoad: 120,
    fluidPound: 100,
    buoyantRodWeight: 100,
    dryRodWeight: 110,
    pumpFriction: 110,
    poFluidLoad: 110,
  };

  const totalTableWidth = Object.values(columnWidths).reduce((a, b) => a + b, 0);

  // After table instance destructuring (so rows is available), add effect to scroll highlighted row into view
  useEffect(() => {
    if (!highlightedRow) return;
    scrollToVirtualRow();
  }, [highlightedRow]);
  useEffect(() => {
    setTimeout(() => {
      scrollToVirtualRow();
    }, 1000);
  }, [isChartFullScreen]);
  const scrollToVirtualRow = () => {
    if (!listRef?.current) return;
    const index = rows.findIndex((r) => r.original?.date === highlightedRow);

    // 1️⃣ determine alignment strategy based on position
    let align = 'center';
    if (index === 0) align = 'start';
    else if (index === data.length - 1) align = 'end';

    // 2️⃣ ensure table section is in viewport (body scroll)
    const tableSection = tableWrapperRef?.current;
    if (tableSection) {
      const rect = tableSection.getBoundingClientRect();

      // scroll only if NOT already visible
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        tableSection.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }

    // 3️⃣ force react-window to render row

    if (index >= 0 && listRef.current) {
      // ensure virtualization has applied layout before scrolling
      requestAnimationFrame(() => {
        try {
          listRef.current.scrollToItem(index, 'center');
        } catch (err) {
          // defensive fallback - ignore if method not available
        }
      });
    }
    // 4️⃣ once row is rendered, fix micro-adjustments if row is partially clipped
    setTimeout(() => {
      const rowEl = document.querySelector(`.highlighted-row`);
      if (!rowEl) return;

      const container = getScrollableParent(rowEl);
      if (container === null) return;

      const rowRect = rowEl.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const isAbove = rowRect.top < containerRect.top;
      const isBelow = rowRect.bottom > containerRect.bottom;

      if (isAbove || isBelow) {
        rowEl.scrollIntoView({
          behavior: 'smooth',
          block: align as ScrollLogicalPosition,
        });
      }
    }, 120);
  };
  const getScrollableParent = (el: Element) => {
    let parent = el.parentElement;
    while (parent) {
      const canScroll = parent.scrollHeight > parent.clientHeight;
      if (canScroll) return parent;
      parent = parent.parentElement;
    }
    return document.scrollingElement;
  };

  return (
    <>
      <section className='table-container-wrapper'>
        {!isLoadingCards && (
          <div className='table-container card-table-container'>
            <div className='table-header-wrapper' style={{ width: totalTableWidth }}>
              <table {...getTableProps()} className='data-table card-detail-table'>
                <thead>
                  {headerGroups.map((headerGroup: HeaderGroup<any>) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((col) => {
                        const column = col as unknown as ColumnInstance<any> & UseSortByColumnProps<any> as any;
                        return (
                          <th
                            {...column.getHeaderProps(column.getSortByToggleProps())}
                            className={`${column.id}-column`}
                            style={{ width: columnWidths[column.id] }}
                          >
                            <div className='flex items-center card-detail'>
                              <span className='text-[12px] font-semibold text-[#f7f9f9]'>
                                {column.render('Header')}
                              </span>
                              {column.isSorted && (
                                <span
                                  className={`tdt-sort-icon ${column.isSortedDesc ? 'desc' : 'asc'}`}
                                  style={{ display: column.render('Header') ? 'inline-block' : 'none' }}
                                />
                              )}
                              <span>{column.canFilter ? column.render('Filter') : null}</span>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
              </table>
            </div>
            <div>
              {data.length > 0 && (
                <div
                  className='virtualized-table-body'
                  style={{ width: totalTableWidth, height: listHeight }}
                  ref={tableWrapperRef}
                >
                  <FixedSizeList
                    ref={listRef} // attach ref to list
                    height={listHeight}
                    itemCount={rows.length}
                    itemSize={45}
                    width={totalTableWidth}
                  >
                    {({ index, style }) => {
                      const row = rows[index];
                      prepareRow(row);
                      const seriedClicked = row.original.date === highlightedRow;
                      return (
                        <div
                          {...row.getRowProps({ style })}
                          className={`card-row ${seriedClicked ? 'highlighted-row' : ''}`}
                        >
                          {row.cells.map((cell) => (
                            <div
                              {...cell.getCellProps()}
                              className={`card-cell ${cell.column.id}-column`}
                              style={{
                                width: columnWidths[cell.column.id],
                              }}
                            >
                              {cell.render('Cell')}
                            </div>
                          ))}
                        </div>
                      );
                    }}
                  </FixedSizeList>
                </div>
              )}
              {data.length === 0 && !isLoadingCards && (
                <div className='no-matching-records-container text-center p-4 pl-6 pr-6 h-[450px] flex items-center justify-center'>
                  <span className='no-matching-records-message'>
                    No matching records found, Adjust the time selection
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        {isLoadingCards && (
          <div className='no-matching-records-container text-center p-4 pl-6 pr-6 h-[400px] flex items-center justify-center'>
            <Loader />
          </div>
        )}
      </section>
    </>
  );
};

export default CardDateTable;
